import React from 'react';
import { useUIOptional } from '../../contexts/UIContext';

interface FilterTogglesGroupProps {
  showDependencies: boolean;
  onShowDependencies: (val: boolean) => void;
  hideEmptyDays: boolean;
  onHideEmptyDays: (val: boolean) => void;
  showCompleted: boolean;
  onToggleShowCompleted: (val: boolean) => void;
  showArchived: boolean;
  onToggleShowArchived: (val: boolean) => void;
  showTaskDetails: boolean;
  onToggleShowTaskDetails: (val: boolean) => void;
}

export const FilterTogglesGroup: React.FC<FilterTogglesGroupProps> = ({
  showDependencies,
  onShowDependencies,
  hideEmptyDays,
  onHideEmptyDays,
  showCompleted,
  onToggleShowCompleted,
  showArchived,
  onToggleShowArchived,
  showTaskDetails,
  onToggleShowTaskDetails
}) => {
  const uiCtx = useUIOptional();
  const uiMode = uiCtx?.uiMode ?? 'standard';
  const setUiMode = uiCtx?.setUiMode;

  return (
    <div className="flex items-center gap-4 shrink-0">
      <label 
        title="Zeigt oder blendet die Verknüpfungslinien (Abhängigkeiten) zwischen Aufgaben im Gantt-Chart aus"
        className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${!showDependencies ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
      >
        <div className="relative flex items-center">
          <input 
            type="checkbox" 
            checked={showDependencies} 
            onChange={(e) => onShowDependencies(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          {!showDependencies && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <span className={`text-[9px] font-bold transition-colors ${!showDependencies ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
          Abhängigkeiten
        </span>
      </label>

      <label 
        title="Blendet Tage ohne Aufgaben im Gantt-Zeitstrahl aus, um eine kompaktere Ansicht zu erhalten"
        className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${hideEmptyDays ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
      >
        <div className="relative flex items-center">
          <input 
            type="checkbox" 
            checked={hideEmptyDays} 
            onChange={(e) => onHideEmptyDays(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          {hideEmptyDays && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <span className={`text-[9px] font-bold transition-colors ${hideEmptyDays ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
          Leere
        </span>
      </label>

      <label 
        title="Zeigt oder blendet Aufgaben ein, die bereits als erledigt markiert wurden"
        className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${showCompleted ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
      >
        <div className="relative flex items-center">
          <input 
            type="checkbox" 
            checked={showCompleted} 
            onChange={(e) => onToggleShowCompleted(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          {showCompleted && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <span className={`text-[9px] font-bold transition-colors ${showCompleted ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
          Erledigte
        </span>
      </label>

      <label 
        title="Zeigt oder blendet archivierte Aufgaben ein"
        className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${showArchived ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
      >
        <div className="relative flex items-center">
          <input 
            type="checkbox" 
            checked={showArchived} 
            onChange={(e) => onToggleShowArchived(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          {showArchived && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <span className={`text-[9px] font-bold transition-colors ${showArchived ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
          Archivierte
        </span>
      </label>

      <label 
        title="Blendet zusätzliche Details auf Aufgabenkarten ein/aus (z. B. Untertitel, Notizen, Startzeit)"
        className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${!showTaskDetails ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
      >
        <div className="relative flex items-center">
          <input 
            type="checkbox" 
            checked={showTaskDetails} 
            onChange={(e) => onToggleShowTaskDetails(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          {!showTaskDetails && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <span className={`text-[9px] font-bold transition-colors ${!showTaskDetails ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
          Zusatzinfos
        </span>
      </label>

      {setUiMode && (
        <label
          title="Standard: Ruhiges Kartenbild (Details bei Hover) · Experte: Alle Metadaten und Ansichten permanent sichtbar"
          className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg transition-all ${uiMode === 'expert' ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-800' : ''}`}
        >
          <div className="relative flex items-center">
            <input
              type="checkbox"
              data-testid="filterbar-toggle-ui-mode"
              aria-label="Experten-Modus für maximale Informationsdichte"
              checked={uiMode === 'expert'}
              onChange={(e) => setUiMode(e.target.checked ? 'expert' : 'standard')}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
            {uiMode === 'expert' && <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
          </div>
          <span className={`text-[9px] font-bold transition-colors ${uiMode === 'expert' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400 hover:text-indigo-600'}`}>
            Experte
          </span>
        </label>
      )}
    </div>
  );
};
